import LiveMethodExample from "features/onboarding/guided/LiveMethodExample";
import {
  ListStepLayout as ListsLayout,
  HeadingBlock,
  HeroTitle,
  Lead,
  Eyebrow,
} from "features/onboarding/guided/GuidedWelcome";
import Flex from "common/Boxes/Flex";
import {
  SKIP_ONBOARDING_DCA_BOT,
  TRADING_METHOD_TO_FULL_LIST,
} from "@shared/features/onboarding/constants";
import { TradingMethodType } from "@shared/features/onboarding/types";
import Text from "common/Typography/Text";
import Link from "common/Typography/Link";
import React, { useState } from "react";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import SkipOnboardingModal from "features/onboarding/common/SkipOnboardingModal";
import { TRADING_METHOD_TO_FULL_LIST_PATH } from "features/onboarding/common/constants";
import { useSelector } from "react-redux";
import { OnboardingStep } from "analytics/events";
import { getSkipOnboardingFollowGroupModalContent } from "@shared/features/onboarding/utils";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { ONBOARDING_MARKETPLACE_PROMPT_ID } from "testIds";
import styled from "styled-components";
import { revealChildren } from "features/onboarding/common/onboardingUi";

interface MethodSettingsLayoutProps {
  tradingMethod: TradingMethodType;
  options: React.JSX.Element;
}

const MethodSettingsLayout = ({
  tradingMethod,
  options,
}: MethodSettingsLayoutProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);

  const [skipOnboardingModalVisible, setSkipOnboardingModalVisible] =
    useState(false);

  const getSkipModalContent = () => {
    if (tradingMethod === TradingMethodType.SIGNALS_GROUP) {
      return getSkipOnboardingFollowGroupModalContent(user, t);
    } else if (tradingMethod === TradingMethodType.DCA_BOT)
      return t(SKIP_ONBOARDING_DCA_BOT);
  };

  const skipModal = (
    <SkipOnboardingModal
      isVisible={skipOnboardingModalVisible}
      content={getSkipModalContent()}
      onCancel={() => setSkipOnboardingModalVisible(false)}
      newPath={TRADING_METHOD_TO_FULL_LIST_PATH[tradingMethod]}
      onboardingStep={OnboardingStep.METHOD_SETTINGS}
    />
  );

  const optionsWithFooter = (
    <Options
      $flexDirection="column"
      $alignItems={isMobile ? "center" : "start"}
    >
      {options}
      <Flex
        $flexDirection={isMobile ? "column" : "row"}
        style={{ marginLeft: 20 }}
      >
        <Text style={{ marginRight: 4 }}>{t("intent.more_options")}</Text>
        <Link
          onClick={() => setSkipOnboardingModalVisible(true)}
          data-testid={ONBOARDING_MARKETPLACE_PROMPT_ID}
        >
          {t(TRADING_METHOD_TO_FULL_LIST[tradingMethod])}
        </Link>
      </Flex>
    </Options>
  );

  return (
    <ListsLayout>
      <HeadingBlock>
        <Eyebrow>
          {t(
            tradingMethod === TradingMethodType.SIGNALS_GROUP
              ? "intent.signals.name"
              : "intent.dca.name"
          )}
        </Eyebrow>
        <HeroTitle>
          {t(
            tradingMethod === TradingMethodType.SIGNALS_GROUP
              ? "intent.signals_catalog_title"
              : "intent.dca_catalog_title"
          )}
        </HeroTitle>
        <Lead>
          {t(
            tradingMethod === TradingMethodType.SIGNALS_GROUP
              ? "intent.signals_catalog_description"
              : "intent.dca_catalog_description"
          )}
        </Lead>
      </HeadingBlock>
      <LiveMethodExample
        method={tradingMethod === TradingMethodType.DCA_BOT ? "dca" : "signals"}
      />
      {optionsWithFooter}
      {skipModal}
    </ListsLayout>
  );
};

export default MethodSettingsLayout;

const Options = styled(Flex)`
  ${revealChildren(3, 6)};
`;
